import React from 'react';
import {
    Container,
    Typography,
    Box,
    Divider,
    Paper,
    List,
    ListItem,
    ListItemText,
    ListItemIcon
} from '@mui/material';
import {
    CheckCircle,
    Warning,
    Info,
    Security,
    AccountBalance,
    School,
    Payment
} from '@mui/icons-material';

const TermsAndConditions = () => {
    return (
        <Container maxWidth="lg" sx={{ py: 6 }}>
            <Paper elevation={3} sx={{ p: 4 }}>
                <Typography variant="h3" gutterBottom sx={{
                    fontWeight: 700,
                    color: 'primary.main',
                    textAlign: 'center',
                    mb: 2
                }}>
                    Terms and Conditions
                </Typography>

                <Typography variant="subtitle1" sx={{
                    textAlign: 'center',
                    color: 'text.secondary',
                    fontStyle: 'italic',
                    mb: 4
                }}>
                    Last updated: June 28, 2025
                </Typography>

                <Divider sx={{ mb: 4 }} />

                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                    Welcome to the <strong>National Youth Cooperative Society (NYCS) Surat</strong> website.
                    By accessing and using our services, you agree to comply with and be bound by the following
                    terms and conditions. Please read these terms carefully before using our services.
                </Typography>

                <Box sx={{ mt: 4 }}>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main', display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Info sx={{ mr: 1 }} />
                        1. Acceptance of Terms
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ ml: 4 }}>
                        By accessing our website, registering for membership, or using any of our services,
                        you acknowledge that you have read, understood, and agree to be bound by these Terms
                        and Conditions and our Privacy Policy.
                    </Typography>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Box sx={{ mt: 4 }}>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main', display: 'flex', alignItems: 'center', mb: 2 }}>
                        <AccountBalance sx={{ mr: 1 }} />
                        2. Membership Services
                    </Typography>
                    <List sx={{ ml: 2 }}>
                        <ListItem>
                            <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                            <ListItemText
                                primary="Eligibility"
                                secondary="Membership is open to individuals aged 18 years and above who meet our eligibility criteria."
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                            <ListItemText
                                primary="Application Process"
                                secondary="All membership applications must be completed accurately with valid documentation."
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                            <ListItemText
                                primary="Approval Rights"
                                secondary="NYCS Surat reserves the right to approve or reject membership applications at its discretion."
                            />
                        </ListItem>
                    </List>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Box sx={{ mt: 4 }}>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main', display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Payment sx={{ mr: 1 }} />
                        3. Financial Services and Payments
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ ml: 4 }}>
                        <strong>Savings Account:</strong> We offer savings account services to eligible members.
                        All accounts are subject to cooperative banking regulations and internal policies.
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ ml: 4 }}>
                        <strong>Digital Payments:</strong> All payments are processed through secure digital
                        payment gateways (Razorpay). Transaction fees may apply as per the service provider's terms.
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ ml: 4 }}>
                        <strong>Refund Policy:</strong> Refunds, if applicable, will be processed according to
                        our refund policy and may take 5-10 business days to reflect in your account.
                    </Typography>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Box sx={{ mt: 4 }}>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main', display: 'flex', alignItems: 'center', mb: 2 }}>
                        <School sx={{ mr: 1 }} />
                        4. Training and Employment Programs
                    </Typography>
                    <List sx={{ ml: 2 }}>
                        <ListItem>
                            <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                            <ListItemText
                                primary="Program Participation"
                                secondary="Participation in training programs is subject to availability and eligibility criteria."
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                            <ListItemText
                                primary="Attendance Requirements"
                                secondary="Regular attendance is mandatory for all enrolled programs. Failure to attend may result in program termination."
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                            <ListItemText
                                primary="Certification"
                                secondary="Certificates are awarded only upon successful completion of program requirements."
                            />
                        </ListItem>
                    </List>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Box sx={{ mt: 4 }}>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main', display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Security sx={{ mr: 1 }} />
                        5. Data Protection and Privacy
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ ml: 4 }}>
                        Your personal information is collected and processed in accordance with our Privacy Policy.
                        We use Firebase for secure data storage and implement industry-standard security measures
                        to protect your information.
                    </Typography>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Box sx={{ mt: 4 }}>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
                        6. User Responsibilities
                    </Typography>
                    <List sx={{ ml: 2 }}>
                        <ListItem>
                            <ListItemIcon><Warning color="warning" /></ListItemIcon>
                            <ListItemText
                                primary="Accurate Information"
                                secondary="You must provide accurate and complete information during registration and maintain updated records."
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><Warning color="warning" /></ListItemIcon>
                            <ListItemText
                                primary="Account Security"
                                secondary="You are responsible for maintaining the confidentiality of your account credentials."
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><Warning color="warning" /></ListItemIcon>
                            <ListItemText
                                primary="Compliance"
                                secondary="You must comply with all applicable laws and regulations while using our services."
                            />
                        </ListItem>
                    </List>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Box sx={{ mt: 4 }}>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
                        7. Prohibited Activities
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ ml: 4 }}>
                        Users are prohibited from: using the service for illegal activities, providing false information,
                        attempting to breach security measures, interfering with the proper functioning of the website,
                        or engaging in any activity that may harm NYCS Surat or other users.
                    </Typography>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Box sx={{ mt: 4 }}>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
                        8. Limitation of Liability
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ ml: 4 }}>
                        NYCS Surat shall not be liable for any indirect, incidental, special, consequential, or
                        punitive damages arising from your use of our services. Our total liability shall not
                        exceed the amount paid by you for the specific service in question.
                    </Typography>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Box sx={{ mt: 4 }}>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
                        9. Termination
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ ml: 4 }}>
                        We reserve the right to terminate or suspend your account and access to our services
                        immediately, without prior notice, for any breach of these Terms and Conditions or
                        for any other reason at our sole discretion.
                    </Typography>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Box sx={{ mt: 4 }}>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
                        10. Governing Law
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ ml: 4 }}>
                        These Terms and Conditions are governed by the laws of India. Any disputes arising
                        from these terms shall be subject to the exclusive jurisdiction of the courts in
                        Surat, Gujarat, India.
                    </Typography>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Box sx={{ mt: 4 }}>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
                        11. Changes to Terms
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ ml: 4 }}>
                        We reserve the right to update or modify these Terms and Conditions at any time.
                        Changes will be posted on this page with the updated date. Continued use of our
                        services after changes constitutes acceptance of the new terms.
                    </Typography>
                </Box>

                <Divider sx={{ my: 4 }} />

                <Box sx={{
                    mt: 4,
                    p: 3,
                    backgroundColor: 'primary.light',
                    borderRadius: 2,
                    color: 'primary.contrastText'
                }}>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                        Contact Information
                    </Typography>
                    <Typography variant="body1" paragraph>
                        For questions about these Terms and Conditions, please contact us at:
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        <strong>National Youth Cooperative Society (NYCS) Surat</strong><br />
                        📧 Email:jananidhi.29surat@gmail.com<br />
                        📍 Address: G-26, Magnus Shopping Centre, Althan Bhimrad Canal Road, Althan Surat, 395017<br />
                        🌐 Website: https://nycsyuvasurat.com/
                    </Typography>
                </Box>

                <Box sx={{ mt: 4, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        By using our services, you acknowledge that you have read and understood these Terms and Conditions.
                    </Typography>
                </Box>
            </Paper>
        </Container>
    );
};

export default TermsAndConditions;